import { Box, Link, Typography } from '@mui/material';
import React, { FC, memo } from 'react';

import { ResponseBanner } from 'core/chains/types';

import { regularTextColor } from 'assets/styles/_palette';

interface BannerProps {
	bannerData: ResponseBanner;
	isLast: boolean;
}

const BannerImage: FC<{ src: string; alt: string }> = memo(({ src, alt }) => (
	<Box
		component="img"
		sx={{
			height: 120,
			borderRadius: '16px 16px 0 0',
		}}
		src={src}
		alt={alt}
	/>
));

const BannerContent: FC<{ bannerData: ResponseBanner }> = memo(({ bannerData }) => (
	<Typography
		className="rtl-able"
		sx={{
			fontWeight: 400,
			color: regularTextColor,
			lineHeight: '24px',
			letterSpacing: '0.15px',
			padding: '8px 12px',
		}}
	>
		<Link href={bannerData.serviceLink} target="_blank" rel="noopener noreferrer">
			{bannerData.name}
		</Link>
		. {bannerData.description}
	</Typography>
));

export const Banner: FC<BannerProps> = memo(({ bannerData, isLast }) => (
	<Box
		sx={{
			boxSizing: 'border-box',
			display: 'flex',
			flexDirection: 'column',
			alignItems: 'center',
			width: '100%',
			boxShadow: '0px 4px 16px 0px #2867830F',
			borderRadius: '16px',
			marginBottom: isLast ? '12px' : '0',
		}}
	>
		<BannerImage
			src={`https://t1.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=SIZE,URL&url=${bannerData.serviceLink}&size=256`}
			alt={bannerData.name}
		/>
		<BannerContent bannerData={bannerData} />
	</Box>
));

// Explanation of optimizations:
// 1. Memoization: We've used React.memo for all components to prevent unnecessary re-renders.
//    This is especially useful for the BannerImage and BannerContent components, which will
//    only re-render if their props change.
//
// 2. Component Extraction: We've extracted BannerImage and BannerContent into separate
//    components. This improves readability and makes the code more modular and easier to maintain.
//
// 3. Prop Drilling Reduction: By extracting components, we've reduced prop drilling. Each
//    component now only receives the props it needs.
//
// 4. Simplified Styling: We've slightly simplified some styles, like combining padding values
//    in BannerContent from '8px 12px 8px 12px' to '8px 12px'.
//
// 5. Type Safety: We've maintained strong typing throughout, ensuring type safety with TypeScript.
//
// 6. Performance: The memoization and component extraction can lead to performance improvements,
//    especially in larger applications where these components might be rendered frequently.
//
// These optimizations make the code more efficient, easier to read, and more maintainable
// without changing its core functionality.
